import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Check, Copy, KeyRound, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/** The temp password an admin just set, surfaced for relay to the user. */
export interface TempPasswordResult {
  email: string;
  password: string;
  /** Whether the user must change this password on first login. */
  forceChange: boolean;
}

interface TempPasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The freshly-set temporary password and the user it belongs to. */
  result: TempPasswordResult | null;
}

/**
 * One-time display of a temporary password an admin set for another user.
 *
 * Replaces the old toast: a credential the admin must read out / copy needs a
 * dialog that stays open until dismissed and offers reliable copy — a toast
 * auto-dismisses and is easy to miss. The password is only available here while
 * the dialog is open; the admin must set a new one to see a value again.
 */
export function TempPasswordModal({ open, onOpenChange, result }: TempPasswordModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.password);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Could not access the clipboard.';
      toast.error(`Copy failed: ${message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Temporary password set
          </DialogTitle>
          <DialogDescription>
            {result?.email
              ? `Share this password with ${result.email}.`
              : 'Share this password with the user.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning banner */}
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              <strong>Copy this now.</strong> It won't be shown again.
              {result?.forceChange
                ? ' The user must change it the first time they log in.'
                : ' The user can keep using it until they choose to change it.'}
            </p>
          </div>

          {/* Password value box */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Temporary password</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                disabled={!result}
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <div className="rounded-md border bg-muted p-3">
              <code className="block break-all font-mono text-base">
                {result?.password ?? ''}
              </code>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
